// src/components/PaymentForm.js
import React, { useState, useEffect, useRef } from 'react';
import './PaymentForm.css';

// Conditionally import the real or mock service based on environment
const isLocalDevelopment = 
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1';

// Import the appropriate service
let paymentService;
if (isLocalDevelopment) {
  // Use mock service in development
  paymentService = require('../services/PaymentServiceMock');
} else {
  // Use real service in production
  paymentService = require('../services/PaymentService');
}

const { getStripe, createPaymentIntent, recordPayment } = paymentService;

const PaymentForm = ({ listingId, price, userId, listerEmail, remainingCapacity = null, onPaymentSuccess, onPaymentError }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const cardElementRef = useRef(null);
  const cardElement = useRef(null);
  const [quantity, setQuantity] = useState(1);

  // Load Stripe when component mounts
  useEffect(() => {
    const loadStripeElements = async () => {
      try {
        const stripeInstance = await getStripe();
        setStripe(stripeInstance);
        
        // Create Elements
        const elementsInstance = stripeInstance.elements();
        setElements(elementsInstance);
      } catch (error) {
        console.error('Error loading Stripe:', error);
        setError('Failed to load payment system. Please try again later.');
      }
    };
    
    loadStripeElements();
    
    return () => {
      // Cleanup card element if it exists
      if (cardElement.current) {
        cardElement.current.destroy();
      }
    };
  }, []);

  // Create payment intent when component mounts or quantity changes
  useEffect(() => {
    const getPaymentIntent = async () => {
      if (!userId || !listingId || !price) return;
      
      try {
        setLoading(true);
        const { clientSecret, paymentIntentId } = await createPaymentIntent(
          listingId,
          price,
          userId,
          listerEmail, // Pass lister email to payment intent
          quantity
        );
        
        setClientSecret(clientSecret);
        setPaymentIntentId(paymentIntentId);
        setLoading(false);
        
        // Create card element once we have the client secret
        if (elements && !cardElement.current) {
          setTimeout(() => {
            try {
              cardElement.current = elements.create('card');
              cardElement.current.mount('#card-element');
              
              // Add test card info for development
              if (isLocalDevelopment) {
                const helperText = document.querySelector('.card-element-help');
                if (helperText) {
                  helperText.innerHTML = 'Development mode: any card details will work';
                }
              }
            } catch (err) {
              console.error('Error mounting card element:', err);
              setError('Error setting up payment form. Please try again.');
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setError('Failed to prepare payment. Please try again.');
        setLoading(false);
      }
    };
    
    if (elements && !clientSecret) {
      getPaymentIntent();
    }
  }, [listingId, price, userId, listerEmail, elements, clientSecret, quantity]);

  // Reset intent when quantity changes so we can recreate with new amount
  useEffect(() => {
    setClientSecret('');
    setPaymentIntentId(null);
  }, [quantity]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }
    
    setProcessing(true);
    setError(null);
    setMessage('');
    
    try {
      // Confirm payment with the card element
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement.current,
        }
      });
      
      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        if (onPaymentError) onPaymentError(stripeError.message);
        return;
      }
      
      if (paymentIntent.status === 'succeeded') {
        try {
          // Record the payment in our database
          await recordPayment({
            listingId,
            userId,
            amount: price * quantity,
            paymentIntentId: paymentIntentId || paymentIntent.id,
            status: 'succeeded',
            listerEmail: listerEmail,  // Include lister email in the payment record
            quantity
          });
          
          setMessage('Payment successful! Redirecting...');
          
          if (onPaymentSuccess) {
            setTimeout(() => onPaymentSuccess({ paymentIntent, quantity }), 1200);
          }
        } catch (recordError) {
          console.error('Payment recording error:', recordError);
          const msg = String(recordError?.message || recordError);

          // Treat permission errors as non-fatal; webhook will persist data
          if (/permission|insufficient|denied/i.test(msg)) {
            console.warn('Proceeding despite record permission error; webhook will finalize.');
            setMessage('Payment successful! Finalizing...');
            if (onPaymentSuccess) {
              setTimeout(() => onPaymentSuccess({ paymentIntent, quantity }), 1000);
            }
          } else if (isLocalDevelopment) {
            console.warn('Development mode: Continuing despite record error');
            setMessage('Mock payment successful! Redirecting...');
            if (onPaymentSuccess) {
              setTimeout(() => onPaymentSuccess(paymentIntent), 1000);
            }
          } else {
            setError('Error recording payment. Please contact support.');
            if (onPaymentError) onPaymentError('Payment recording failed');
          }
        }
      } else {
        setError('Payment processing failed. Please try again.');
        if (onPaymentError) onPaymentError('Payment processing failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError('An error occurred during payment processing. Please try again.');
      if (onPaymentError) onPaymentError(error.message);
    }
    
    setProcessing(false);
  };

  if (loading) {
    return <div className="payment-loading">Preparing payment...</div>;
  }

  if (error && !message) {
    return (
      <div className="payment-error">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="try-again-button"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Add environment indicator for local development
  const environmentLabel = isLocalDevelopment ? 
    <div className="payment-environment-label">🔧 Development Mode (Mock Payments)</div> : null;

  return (
    <div className="payment-form-container">
      {environmentLabel}
      <form onSubmit={handleSubmit} className="payment-form">
        <h3>Complete Payment</h3>
        <div className="quantity-group">
          <label htmlFor="ticket-quantity">Quantity</label>
          <input
            id="ticket-quantity"
            type="number"
            min={1}
            max={remainingCapacity ?? undefined}
            value={quantity}
            onChange={(e) => {
              let val = parseInt(e.target.value || '1', 10);
              if (isNaN(val) || val < 1) val = 1;
              if (remainingCapacity != null) {
                val = Math.min(val, remainingCapacity);
              }
              setQuantity(val);
            }}
          />
          {remainingCapacity != null && (
            <div className="capacity-hint">{remainingCapacity} ticket(s) remaining</div>
          )}
        </div>
        <p className="payment-amount">Amount: ${(Number.parseFloat(price ?? 0) * quantity).toFixed(2)} CAD</p>
        <p className="payment-lister">Provider Email: {listerEmail}</p>
        
        <div className="form-group">
          <label htmlFor="card-element">Credit or debit card</label>
          <div id="card-element" ref={cardElementRef} className="card-element"></div>
          <div className="card-element-help">
            {isLocalDevelopment 
              ? 'Development mode: Enter any values' 
              : ''
            }
          </div>
        </div>
        
        {error && <div className="payment-error-message">{error}</div>}
        {message && <div className="payment-success-message">{message}</div>}
        
        <button 
          type="submit" 
          disabled={!stripe || processing || message}
          className="payment-submit-button"
        >
          {processing ? 'Processing...' : 'Pay Now'}
        </button>
        
        <div className="payment-secure-notice">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span>{isLocalDevelopment ? 'Mock payment system (no actual charges)' : 'Secure payment processed by Stripe'}</span>
        </div>
      </form>
      
      <style jsx="true">{`
        .payment-environment-label {
          background-color: #fdfd96;
          padding: 8px 12px;
          border-radius: 4px;
          margin-bottom: 15px;
          text-align: center;
          font-weight: bold;
          color: #333;
        }
        
        .payment-lister {
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 1rem;
        }
      `}</style>
    </div>
  );
};

export default PaymentForm;
